import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import usePageTitle from '../hooks/usePageTitle';
import './MultiSourceBuilder.css';

// Odoo Source Configuration Component
const OdooSourceConfig = ({ config, onChange, sourceName, onNameChange }) => {
  const [models, setModels] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedFields, setExpandedFields] = useState({});
  const [relatedFields, setRelatedFields] = useState({});
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  useEffect(() => {
    fetchOdooModels();
  }, []);

  useEffect(() => {
    if (config.model) {
      fetchModelFields(config.model);
      setSearchTerm(''); // Clear search when model changes
    }
  }, [config.model]);

  const fetchOdooModels = async () => {
    try {
      const response = await axiosInstance.get('/etl/odoo-models/');
      setModels(response.data.models || []);
    } catch (error) {
      console.error('Failed to fetch models:', error);
    }
  };

  const fetchModelFields = async (model) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/etl/odoo-models/${model}/fields/`);
      setFields(response.data.fields || []);
    } catch (error) {
      console.error('Failed to fetch fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedFields = async (fieldName) => {
    const field = fields.find(f => f.name === fieldName);
    if (!field || !field.is_relational || !field.related_model) return;

    setLoading(true);
    try {
      const response = await axiosInstance.get(`/etl/odoo-models/${field.related_model}/fields/`);
      setRelatedFields({
        ...relatedFields,
        [fieldName]: response.data.fields || []
      });
    } catch (error) {
      console.error('Failed to fetch related fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFieldExpansion = async (fieldName) => {
    const isExpanded = expandedFields[fieldName];
    setExpandedFields({
      ...expandedFields,
      [fieldName]: !isExpanded
    });

    if (!isExpanded && !relatedFields[fieldName]) {
      await fetchRelatedFields(fieldName);
    }
  };

  const toggleField = (fieldName, isRelated = false, parentField = null) => {
    const currentFields = config.fields || [];
    const fullFieldName = isRelated ? `${parentField}.${fieldName}` : fieldName;
    
    const newFields = currentFields.includes(fullFieldName)
      ? currentFields.filter(f => f !== fullFieldName)
      : [...currentFields, fullFieldName];
    onChange({ fields: newFields });
  };

  const selectAllFields = () => {
    // If search is active, only select filtered fields
    const fieldsToSelect = searchTerm ? filteredFields.map(f => f.name) : fields.map(f => f.name);
    const currentFields = config.fields || [];
    // Merge with existing selections
    const newFields = [...new Set([...currentFields, ...fieldsToSelect])];
    onChange({ fields: newFields });
  };

  const clearAllFields = () => {
    onChange({ fields: [] });
  };

  // Filter fields based on search term
  const filteredFields = fields.filter(field => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      field.name.toLowerCase().includes(search) ||
      field.type.toLowerCase().includes(search) ||
      (field.label && field.label.toLowerCase().includes(search))
    );
  });

  // Group fields by prefix (part before underscore)
  const groupFieldsByPrefix = (fieldsList) => {
    const groups = {};
    const ungrouped = [];
    
    fieldsList.forEach(field => {
      const parts = field.name.split('_');
      if (parts.length > 1) {
        const prefix = parts[0];
        if (!groups[prefix]) {
          groups[prefix] = [];
        }
        groups[prefix].push(field);
      } else {
        ungrouped.push(field);
      }
    });
    
    // Only keep groups with 3+ fields
    const significantGroups = {};
    Object.keys(groups).forEach(prefix => {
      if (groups[prefix].length >= 3) {
        significantGroups[prefix] = groups[prefix];
      } else {
        ungrouped.push(...groups[prefix]);
      }
    });
    
    return { groups: significantGroups, ungrouped };
  };

  const { groups: fieldGroups, ungrouped: ungroupedFields } = groupFieldsByPrefix(filteredFields);

  const toggleGroup = (groupName) => {
    setCollapsedGroups({
      ...collapsedGroups,
      [groupName]: !collapsedGroups[groupName]
    });
  };

  const collapseAllGroups = () => {
    const allCollapsed = {};
    Object.keys(fieldGroups).forEach(group => {
      allCollapsed[group] = true;
    });
    setCollapsedGroups(allCollapsed);
  };

  const expandAllGroups = () => {
    setCollapsedGroups({});
  };

  const areAllGroupsCollapsed = Object.keys(fieldGroups).length > 0 && 
    Object.keys(fieldGroups).every(group => collapsedGroups[group]);

  // Detect missing write_date fields for incremental loads
  const getMissingWriteDates = () => {
    const selectedFields = config.fields || [];
    const missing = [];
    
    // Check main model write_date
    if (!selectedFields.includes('write_date') && !selectedFields.includes('__last_update')) {
      missing.push('write_date');
    }
    
    // Check related fields
    const relatedFields = selectedFields.filter(f => f.includes('.'));
    const relatedObjects = new Set(relatedFields.map(f => f.split('.')[0]));
    
    relatedObjects.forEach(relatedField => {
      const writeDateField = `${relatedField}.write_date`;
      if (!selectedFields.includes(writeDateField)) {
        missing.push(writeDateField);
      }
    });
    
    return missing;
  };

  const missingWriteDates = getMissingWriteDates();

  const fetchPreviewData = async () => {
    if (!config.model) {
      alert('Please select an Odoo model first');
      return;
    }

    if (!config.fields || config.fields.length === 0) {
      alert('Please select at least one field before previewing');
      return;
    }

    setLoadingPreview(true);
    try {
      const response = await axiosInstance.post('/etl/preview-data/', {
        model: config.model,
        fields: config.fields,
        domain: config.domain || '[]',
        limit: 10
      });
      setPreviewData(response.data.records || []);
      setShowPreview(true);
    } catch (error) {
      console.error('Failed to fetch preview:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert('Failed to fetch preview data:\n\n' + errorMsg);
    } finally {
      setLoadingPreview(false);
    }
  };

  return (
    <div className="config-form">
      {/* Source Name Editor */}
      <div className="form-group">
        <label>Source Name</label>
        <div className="name-editor">
          {isEditingName ? (
            <input
              type="text"
              value={sourceName}
              onChange={(e) => onNameChange(e.target.value)}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setIsEditingName(false);
                if (e.key === 'Escape') setIsEditingName(false);
              }}
              className="form-input"
              autoFocus
            />
          ) : (
            <div className="name-display" onClick={() => setIsEditingName(true)}>
              <span className="source-name-text">{sourceName}</span>
              <button type="button" className="btn-edit-name">✏️</button>
            </div>
          )}
        </div>
      </div>

      <div className="form-group">
        <label>Odoo Model</label>
        <select
          value={config.model || ''}
          onChange={(e) => onChange({ model: e.target.value, fields: [] })}
          className="form-select"
        >
          <option value="">Select a model...</option>
          {models.map((model) => (
            <option key={model} value={model}>{model}</option>
          ))}
        </select>
      </div>

      {config.model && (
        <>
          {/* Two Column Layout: Available Fields + Selected Fields */}
          <div className="form-group">
            <div className="fields-container-split">
              {/* Left: Available Fields */}
              <div className="available-fields-panel">
                <div className="panel-header">
                  <h3>Available Fields ({fields.length})</h3>
                  <div className="panel-actions">
                    {Object.keys(fieldGroups).length > 0 && (
                      <button 
                        onClick={areAllGroupsCollapsed ? expandAllGroups : collapseAllGroups} 
                        className="btn-field-action-small" 
                        type="button"
                        title={areAllGroupsCollapsed ? "Expand all groups" : "Collapse all groups"}
                      >
                        {areAllGroupsCollapsed ? '▼' : '▲'}
                      </button>
                    )}
                    <button 
                      onClick={selectAllFields} 
                      className="btn-field-action" 
                      type="button"
                    >
                      {searchTerm ? `Add Filtered (${filteredFields.length})` : 'Add All'}
                    </button>
                  </div>
                </div>

                {/* Search Box */}
                <div className="search-box">
                  <input
                    type="text"
                    placeholder="🔍 Search fields..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  {searchTerm && (
                    <button 
                      onClick={() => setSearchTerm('')} 
                      className="btn-clear-search"
                      type="button"
                    >
                      ×
                    </button>
                  )}
                </div>

                {searchTerm && (
                  <div className="search-results-info">
                    {filteredFields.length} of {fields.length} fields
                  </div>
                )}
                
                {loading ? (
                  <div className="loading">Loading fields...</div>
                ) : filteredFields.length === 0 ? (
                  <div className="no-fields-found">
                    {searchTerm ? `No fields matching "${searchTerm}"` : 'No fields available'}
                  </div>
                ) : (
                  <div className="fields-list">
                    {/* Render ungrouped fields FIRST */}
                    {ungroupedFields.length > 0 && (
                      <div className="field-group ungrouped-section">
                        <div className="field-group-header ungrouped-header">
                          <span className="field-group-title">⭐ Core Fields</span>
                          <span className="field-group-count">{ungroupedFields.length}</span>
                        </div>
                        <div className="field-group-items">
                          {ungroupedFields.map((field) => (
                            <div key={field.name} className="field-item">
                              <div className="field-row">
                                <span className="field-name">{field.name}</span>
                                <span className="field-type">{field.type}</span>
                                {field.is_relational && (
                                  <button
                                    className="btn-expand-small"
                                    onClick={() => toggleFieldExpansion(field.name)}
                                    title={`Show ${field.related_model} fields`}
                                    type="button"
                                  >
                                    {expandedFields[field.name] ? '▼' : '▶'}
                                  </button>
                                )}
                                <button
                                  className={`btn-add ${(config.fields || []).includes(field.name) ? 'added' : ''}`}
                                  onClick={() => toggleField(field.name)}
                                  type="button"
                                >
                                  {(config.fields || []).includes(field.name) ? '✓' : '+'}
                                </button>
                              </div>
                              
                              {/* Related fields */}
                              {expandedFields[field.name] && relatedFields[field.name] && (
                                <div className="related-fields">
                                  <div className="related-fields-header">
                                    📎 {field.related_model}
                                  </div>
                                  {relatedFields[field.name].map((relField) => (
                                    <div key={relField.name} className="field-row related">
                                      <span className="field-name">{field.name}.{relField.name}</span>
                                      <span className="field-type">{relField.type}</span>
                                      <button
                                        className={`btn-add ${(config.fields || []).includes(`${field.name}.${relField.name}`) ? 'added' : ''}`}
                                        onClick={() => toggleField(relField.name, true, field.name)}
                                        type="button"
                                      >
                                        {(config.fields || []).includes(`${field.name}.${relField.name}`) ? '✓' : '+'}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Render grouped fields */}
                    {Object.keys(fieldGroups).sort().map((groupName) => (
                      <div key={`group-${groupName}`} className="field-group">
                        <div 
                          className="field-group-header"
                          onClick={() => toggleGroup(groupName)}
                        >
                          <span className={`field-group-toggle ${collapsedGroups[groupName] ? 'collapsed' : ''}`}>
                            ▼
                          </span>
                          <span className="field-group-title">{groupName}_*</span>
                          <span className="field-group-count">{fieldGroups[groupName].length}</span>
                        </div>
                        <div className={`field-group-items ${collapsedGroups[groupName] ? 'collapsed' : ''}`}>
                          {fieldGroups[groupName].map((field) => (
                            <div key={field.name} className="field-item">
                              <div className="field-row">
                                <span className="field-name">{field.name}</span>
                                <span className="field-type">{field.type}</span>
                                {field.is_relational && (
                                  <button
                                    className="btn-expand-small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleFieldExpansion(field.name);
                                    }}
                                    title={`Show ${field.related_model} fields`}
                                    type="button"
                                  >
                                    {expandedFields[field.name] ? '▼' : '▶'}
                                  </button>
                                )}
                                <button
                                  className={`btn-add ${(config.fields || []).includes(field.name) ? 'added' : ''}`}
                                  onClick={() => toggleField(field.name)}
                                  type="button"
                                >
                                  {(config.fields || []).includes(field.name) ? '✓' : '+'}
                                </button>
                              </div>
                              
                              {expandedFields[field.name] && relatedFields[field.name] && (
                                <div className="related-fields">
                                  <div className="related-fields-header">
                                    📎 {field.related_model}
                                  </div>
                                  {relatedFields[field.name].map((relField) => (
                                    <div key={relField.name} className="field-row related">
                                      <span className="field-name">{field.name}.{relField.name}</span>
                                      <span className="field-type">{relField.type}</span>
                                      <button
                                        className={`btn-add ${(config.fields || []).includes(`${field.name}.${relField.name}`) ? 'added' : ''}`}
                                        onClick={() => toggleField(relField.name, true, field.name)}
                                        type="button"
                                      >
                                        {(config.fields || []).includes(`${field.name}.${relField.name}`) ? '✓' : '+'}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                      ))}
                    </div>
                  </div>
                ))}
                  </div>
                )}
              </div>

              {/* Right: Selected Fields */}
              <div className="selected-fields-panel">
                <div className="panel-header">
                  <h3>Selected Fields ({(config.fields || []).length})</h3>
                  <button 
                    onClick={fetchPreviewData} 
                    disabled={!config.fields || config.fields.length === 0 || loadingPreview}
                    className="btn-preview-small"
                    type="button"
                  >
                    {loadingPreview ? '⏳' : '👁️'}
                  </button>
                </div>

                {/* Missing Write Dates Warning */}
                {missingWriteDates.length > 0 && (config.fields || []).length > 0 && (
                  <div className="missing-dates-warning">
                    <div className="warning-header">
                      ⚠️ Missing Date Fields for Incremental Loads
                    </div>
                    <div className="warning-content">
                      Recommended to add:
                      <div className="suggested-fields">
                        {missingWriteDates.map(field => (
                          <button
                            key={field}
                            onClick={() => {
                              if (field.includes('.')) {
                                const [parent, child] = field.split('.');
                                toggleField(child, true, parent);
                              } else {
                                toggleField(field);
                              }
                            }}
                            className="btn-add-suggested"
                            type="button"
                          >
                            + {field}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {(config.fields || []).length === 0 ? (
                  <div className="empty-selection">
                    <p>No fields selected</p>
                    <small>Click + to add fields</small>
                  </div>
                ) : (
                  <div className="selected-fields-list">
                    {(config.fields || []).map((fieldName, idx) => (
                      <div key={fieldName} className="selected-field-item">
                        <span className="field-number">{idx + 1}</span>
                        <span className="field-name">{fieldName}</span>
                        <button
                          className="btn-remove-field"
                          onClick={() => {
                            const isRelated = fieldName.includes('.');
                            if (isRelated) {
                              const [parent, child] = fieldName.split('.');
                              toggleField(child, true, parent);
                            } else {
                              toggleField(fieldName);
                            }
                          }}
                          type="button"
                          title="Remove field"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button 
                      onClick={clearAllFields} 
                      className="btn-clear-all" 
                      type="button"
                    >
                      Clear All
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Domain Filter (optional)</label>
            <input
              type="text"
              placeholder='[["active", "=", true]]'
              value={config.domain || ''}
              onChange={(e) => onChange({ domain: e.target.value })}
              className="form-input"
            />
            <div className="field-note incremental-note">
              <div className="note-header">
                ⏱️ <strong>Incremental Load Strategy</strong>
              </div>
              <div className="note-content">
                <p><strong>For the main model ({config.model || 'selected model'}):</strong></p>
                <ul>
                  <li>Add <code>write_date</code> to detect record changes</li>
                  <li>ETL will filter: <code>[["write_date", "&gt;=", last_run_date]]</code></li>
                </ul>
                
                <p><strong>For related fields (many2one, etc.):</strong></p>
                <ul>
                  <li>Example: If you select <code>category_id.name</code></li>
                  <li>Also add <code>category_id.write_date</code> to track category changes</li>
                  <li>This detects when the category name changes, not just the product</li>
                </ul>
                
                <p className="note-recommendation">
                  💡 <strong>Best Practice:</strong> For each related field like <code>partner_id.name</code>, 
                  also include <code>partner_id.write_date</code> to capture changes in the related record.
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="preview-modal" onClick={() => setShowPreview(false)}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            <div className="preview-header">
              <h3>📊 Data Preview - {config.model}</h3>
              <button onClick={() => setShowPreview(false)} className="btn-close">×</button>
            </div>
            <div className="preview-body">
              {previewData && previewData.length > 0 ? (
                <>
                  <div className="preview-info">
                    <span>Showing {previewData.length} latest records</span>
                    <span className="text-muted">(Including related fields like product_tmpl_id.name)</span>
                  </div>
                  <div className="preview-table-container">
                    <table className="preview-table">
                      <thead>
                        <tr>
                          {Object.keys(previewData[0] || {}).map((field) => (
                            <th key={field}>{field}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.map((record, idx) => (
                          <tr key={idx}>
                            {Object.keys(record).map((field) => (
                              <td key={field}>
                                {Array.isArray(record[field]) 
                                  ? `[${record[field][0]}] ${record[field][1]}`
                                  : typeof record[field] === 'object' && record[field] !== null
                                  ? JSON.stringify(record[field])
                                  : record[field]?.toString() || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="no-data">
                  <p>No data available</p>
                  <small>Check your domain filter or try selecting different fields</small>
                </div>
              )}
            </div>
            <div className="preview-footer">
              <button onClick={() => setShowPreview(false)} className="btn-primary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// CSV Source Configuration Component
const CSVSourceConfig = ({ config, onChange }) => {
  return (
    <div className="config-form">
      <div className="form-group">
        <label>CSV File Path</label>
        <input
          type="text"
          placeholder="/path/to/file.csv or gs://bucket/file.csv"
          value={config.path || ''}
          onChange={(e) => onChange({ path: e.target.value })}
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label>Delimiter</label>
        <select
          value={config.delimiter || ','}
          onChange={(e) => onChange({ delimiter: e.target.value })}
          className="form-select"
        >
          <option value=",">Comma (,)</option>
          <option value=";">Semicolon (;)</option>
          <option value="\t">Tab</option>
          <option value="|">Pipe (|)</option>
        </select>
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={config.has_header !== undefined ? config.has_header : true}
            onChange={(e) => onChange({ has_header: e.target.checked })}
          />
          First row contains headers
        </label>
      </div>

      <div className="form-group">
        <label>Encoding</label>
        <select
          value={config.encoding || 'utf-8'}
          onChange={(e) => onChange({ encoding: e.target.value })}
          className="form-select"
        >
          <option value="utf-8">UTF-8</option>
          <option value="latin1">Latin1</option>
          <option value="iso-8859-1">ISO-8859-1</option>
        </select>
      </div>
    </div>
  );
};

// API Source Configuration Component
const APISourceConfig = ({ config, onChange }) => {
  return (
    <div className="config-form">
      <div className="form-group">
        <label>Base URL</label>
        <input
          type="text"
          placeholder="https://api.example.com"
          value={config.base_url || ''}
          onChange={(e) => onChange({ base_url: e.target.value })}
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label>Endpoint</label>
        <input
          type="text"
          placeholder="/api/v1/data"
          value={config.endpoint || ''}
          onChange={(e) => onChange({ endpoint: e.target.value })}
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label>Authentication Type</label>
        <select
          value={config.auth_type || 'none'}
          onChange={(e) => onChange({ auth_type: e.target.value })}
          className="form-select"
        >
          <option value="none">None</option>
          <option value="bearer">Bearer Token</option>
          <option value="api_key">API Key</option>
          <option value="basic">Basic Auth</option>
        </select>
      </div>

      {config.auth_type === 'bearer' && (
        <div className="form-group">
          <label>Bearer Token</label>
          <input
            type="password"
            placeholder="Enter token..."
            value={config.token || ''}
            onChange={(e) => onChange({ token: e.target.value })}
            className="form-input"
          />
        </div>
      )}

      {config.auth_type === 'api_key' && (
        <>
          <div className="form-group">
            <label>API Key Name</label>
            <input
              type="text"
              placeholder="X-API-Key"
              value={config.api_key_name || ''}
              onChange={(e) => onChange({ api_key_name: e.target.value })}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>API Key Value</label>
            <input
              type="password"
              placeholder="Enter API key..."
              value={config.api_key_value || ''}
              onChange={(e) => onChange({ api_key_value: e.target.value })}
              className="form-input"
            />
          </div>
        </>
      )}

      <div className="form-group">
        <label>HTTP Method</label>
        <select
          value={config.method || 'GET'}
          onChange={(e) => onChange({ method: e.target.value })}
          className="form-select"
        >
          <option value="GET">GET</option>
          <option value="POST">POST</option>
        </select>
      </div>
    </div>
  );
};

// Database Source Configuration Component
const DatabaseSourceConfig = ({ config, onChange }) => {
  return (
    <div className="config-form">
      <div className="form-group">
        <label>Database Type</label>
        <select
          value={config.db_type || 'postgresql'}
          onChange={(e) => onChange({ db_type: e.target.value })}
          className="form-select"
        >
          <option value="postgresql">PostgreSQL</option>
          <option value="mysql">MySQL</option>
          <option value="bigquery">BigQuery</option>
        </select>
      </div>

      <div className="form-group">
        <label>Host</label>
        <input
          type="text"
          placeholder="localhost"
          value={config.host || ''}
          onChange={(e) => onChange({ host: e.target.value })}
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label>Database Name</label>
        <input
          type="text"
          placeholder="database_name"
          value={config.database || ''}
          onChange={(e) => onChange({ database: e.target.value })}
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label>Query</label>
        <textarea
          placeholder="SELECT * FROM table WHERE ..."
          value={config.query || ''}
          onChange={(e) => onChange({ query: e.target.value })}
          className="form-textarea"
          rows={4}
        />
      </div>
    </div>
  );
};

const MultiSourceBuilder = () => {
  usePageTitle('Multi-Source Builder');
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [flowName, setFlowName] = useState('');
  const [flowDescription, setFlowDescription] = useState('');
  const [sources, setSources] = useState([]);
  const [mergeOperations, setMergeOperations] = useState([]);
  const [qualityChecks, setQualityChecks] = useState([]);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [qualityAnalysis, setQualityAnalysis] = useState(null);
  const [analyzingQuality, setAnalyzingQuality] = useState(false);

  const sourceTypes = [
    { type: 'odoo', icon: '🗄️', name: 'Odoo Model', color: '#3b82f6' },
    { type: 'csv', icon: '📄', name: 'CSV File', color: '#10b981' },
    { type: 'api', icon: '🌐', name: 'REST API', color: '#8b5cf6' },
    { type: 'database', icon: '💾', name: 'Database', color: '#f59e0b' },
  ];

  const addSource = (type) => {
    const newSource = {
      id: `source-${Date.now()}`,
      type,
      name: `${type}_source_${sources.length + 1}`,
      config: getDefaultConfig(type)
    };
    setSources([...sources, newSource]);
    setShowSourceModal(false);
    setEditingSource(newSource.id); // Open for editing immediately
  };

  const updateSource = (id, updates) => {
    setSources(sources.map(s => s.id === id ? { ...s, ...updates } : s));
  };

  const updateSourceConfig = (id, configUpdates) => {
    setSources(sources.map(s => 
      s.id === id ? { ...s, config: { ...s.config, ...configUpdates } } : s
    ));
  };

  const getDefaultConfig = (type) => {
    const defaults = {
      odoo: { model: '', fields: [], domain: '' },
      csv: { path: '', delimiter: ',', has_header: true, encoding: 'utf-8' },
      api: { base_url: '', endpoint: '', method: 'GET', auth_type: 'none' },
      database: { db_type: 'postgresql', host: '', database: '', query: '' }
    };
    return defaults[type] || {};
  };

  const addMerge = () => {
    if (sources.length < 2) {
      alert('You need at least 2 data sources to add a merge operation');
      return;
    }

    const newMerge = {
      id: `merge-${Date.now()}`,
      type: 'join',
      name: `Merge ${sources.length}`,
      left_source: sources[0].id,
      right_source: sources[1].id,
      join_type: 'left',
      left_key: '',
      right_key: ''
    };
    setMergeOperations([...mergeOperations, newMerge]);
  };

  const addQualityCheck = (type, field = null, config = {}) => {
    const newCheck = {
      id: `check-${Date.now()}`,
      type,
      field,
      name: field ? `${type}_${field}` : `${type}_check`,
      config,
      threshold: config.threshold || 0.95,
      severity: config.severity || 'warning'
    };
    setQualityChecks([...qualityChecks, newCheck]);
  };

  const removeSource = (id) => {
    setSources(sources.filter(s => s.id !== id));
  };

  const removeMerge = (id) => {
    setMergeOperations(mergeOperations.filter(m => m.id !== id));
  };

  const removeCheck = (id) => {
    setQualityChecks(qualityChecks.filter(c => c.id !== id));
  };

  const analyzeDataQuality = async () => {
    // Only analyze Odoo sources for now
    const odooSource = sources.find(s => s.type === 'odoo');
    if (!odooSource || !odooSource.config.model || !odooSource.config.fields || odooSource.config.fields.length === 0) {
      alert('Please configure at least one Odoo source with fields');
      return;
    }

    setAnalyzingQuality(true);
    try {
      const response = await axiosInstance.post('/etl/analyze-quality/', {
        model: odooSource.config.model,
        fields: odooSource.config.fields,
        domain: odooSource.config.domain || '[]',
        sample_size: 1000
      });
      
      setQualityAnalysis(response.data);
      
      // Auto-add recommended quality checks based on analysis
      const recommendations = response.data.recommendations || [];
      recommendations.forEach(rec => {
        if (!qualityChecks.find(c => c.type === rec.type && c.field === rec.field)) {
          addQualityCheck(rec.type, rec.field, rec.config);
        }
      });
      
    } catch (error) {
      console.error('Failed to analyze quality:', error);
      alert('Failed to analyze data quality: ' + (error.response?.data?.error || error.message));
    } finally {
      setAnalyzingQuality(false);
    }
  };

  const createFlow = async () => {
    if (!flowName) {
      alert('Please enter a flow name');
      return;
    }

    if (sources.length === 0) {
      alert('Please add at least one data source');
      return;
    }

    try {
      const tableName = `stg_${flowName.toLowerCase().replace(/[^a-z0-9_]/g, '_')}`;
      const odooSource = sources.find(s => s.type === 'odoo');
      
      // Prepare the flow configuration
      const flowConfig = {
        name: flowName,
        description: flowDescription,
        status: 'active',
        execution_mode: 'direct_bq',
        
        // Odoo config
        odoo_model: odooSource?.config.model || 'res.partner',
        
        // BigQuery config (only required fields for direct_bq mode)
        bq_project_id: 'lamptw-main',
        bq_dataset: 'stg',
        bq_table: tableName,
        bq_error_dataset: 'error',
        bq_error_table: `${tableName}_errors`,
        
        // Store complete multi-source configuration as JSON
        extraction_config: JSON.stringify({
          sources: sources.map(s => ({
            id: s.id,
            name: s.name,
            type: s.type,
            config: s.config
          })),
          merge_operations: mergeOperations,
          quality_checks: qualityChecks
        })
      };

      const response = await axiosInstance.post('/etl/flows/', flowConfig);
      
      alert(`✅ Flow "${flowName}" created successfully!`);
      navigate('/flows');
      
    } catch (error) {
      console.error('Failed to create flow:', error);
      const errorDetail = error.response?.data;
      console.log('Error details:', errorDetail);
      
      let errorMsg = 'Failed to create flow:\n\n';
      if (typeof errorDetail === 'object') {
        errorMsg += Object.entries(errorDetail).map(([key, value]) => 
          `${key}: ${Array.isArray(value) ? value.join(', ') : value}`
        ).join('\n');
      } else {
        errorMsg += errorDetail || error.message;
      }
      
      alert(errorMsg);
    }
  };

  return (
    <div className="multi-source-container">
      {/* Header */}
      <div className="builder-header">
        <button onClick={() => navigate('/flows/new')} className="btn-back">
          ← Back
        </button>
        <div>
          <h1>Multi-Source Pipeline Builder</h1>
          <p>Combine data from multiple sources with quality checks</p>
        </div>
      </div>

      {/* Simple Progress Tabs */}
      <div className="progress-tabs">
        <button 
          className={`tab ${step === 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}
          onClick={() => setStep(1)}
        >
          <span className="tab-icon">{step > 1 ? '✓' : '1'}</span>
          <span className="tab-text">Data Sources ({sources.length})</span>
        </button>
        <button 
          className={`tab ${step === 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}
          onClick={() => setStep(2)}
          disabled={sources.length === 0}
        >
          <span className="tab-icon">{step > 2 ? '✓' : '2'}</span>
          <span className="tab-text">Merge ({mergeOperations.length})</span>
        </button>
        <button 
          className={`tab ${step === 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}
          onClick={() => setStep(3)}
          disabled={sources.length === 0}
        >
          <span className="tab-icon">{step > 3 ? '✓' : '3'}</span>
          <span className="tab-text">Quality ({qualityChecks.length})</span>
        </button>
        <button 
          className={`tab ${step === 4 ? 'active' : ''}`}
          onClick={() => setStep(4)}
          disabled={sources.length === 0}
        >
          <span className="tab-icon">4</span>
          <span className="tab-text">Review</span>
        </button>
      </div>

      {/* Step 1: Data Sources */}
      {step === 1 && (
        <div className="step-content">
          <div className="step-header">
            <h2>📥 Add Data Sources</h2>
            <button onClick={() => setShowSourceModal(true)} className="btn-primary">
              + Add Source
            </button>
          </div>

          {sources.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔌</div>
              <h3>No data sources yet</h3>
              <p>Add at least one data source to continue</p>
            </div>
          ) : (
            <div className="sources-list">
              {sources.map((source) => (
                <div key={source.id} className="source-card">
                  <div className="source-header">
                    <div className="source-icon">
                      {sourceTypes.find(t => t.type === source.type)?.icon}
                    </div>
                    <div className="source-info">
                      <input
                        type="text"
                        value={source.name}
                        onChange={(e) => updateSource(source.id, { name: e.target.value })}
                        className="source-name-input"
                      />
                      <span className="source-type">
                        {sourceTypes.find(t => t.type === source.type)?.name}
                      </span>
                    </div>
                    <button 
                      onClick={() => setEditingSource(editingSource === source.id ? null : source.id)} 
                      className="btn-config"
                      title="Configure source"
                    >
                      ⚙️
                    </button>
                    <button onClick={() => removeSource(source.id)} className="btn-remove">
                      ×
                    </button>
                  </div>
                  
                  {/* Configuration Form */}
                  {editingSource === source.id && (
                    <div className="source-config-form">
                      {source.type === 'odoo' && (
                        <OdooSourceConfig 
                          config={source.config}
                          onChange={(config) => updateSourceConfig(source.id, config)}
                          sourceName={source.name}
                          onNameChange={(name) => updateSource(source.id, { name })}
                        />
                      )}
                      {source.type === 'csv' && (
                        <CSVSourceConfig 
                          config={source.config}
                          onChange={(config) => updateSourceConfig(source.id, config)}
                        />
                      )}
                      {source.type === 'api' && (
                        <APISourceConfig 
                          config={source.config}
                          onChange={(config) => updateSourceConfig(source.id, config)}
                        />
                      )}
                      {source.type === 'database' && (
                        <DatabaseSourceConfig 
                          config={source.config}
                          onChange={(config) => updateSourceConfig(source.id, config)}
                        />
                      )}
                    </div>
                  )}

                  {/* Preview Summary (when not editing) */}
                  {editingSource !== source.id && (
                    <div className="source-summary">
                      {source.type === 'odoo' && (
                        <div className="summary-item">
                          Model: <code>{source.config.model || 'Not configured'}</code>
                          {source.config.fields?.length > 0 && (
                            <span className="field-count">{source.config.fields.length} fields</span>
                          )}
                        </div>
                      )}
                      {source.type === 'csv' && (
                        <div className="summary-item">
                          Path: <code>{source.config.path || 'Not configured'}</code>
                        </div>
                      )}
                      {source.type === 'api' && (
                        <div className="summary-item">
                          Endpoint: <code>{source.config.base_url || 'Not configured'}{source.config.endpoint || ''}</code>
                        </div>
                      )}
                      {source.type === 'database' && (
                        <div className="summary-item">
                          Database: <code>{source.config.db_type || 'Not configured'}</code>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="step-actions">
            <button onClick={() => setStep(2)} className="btn-primary" disabled={sources.length === 0}>
              Next: Merge & Transform →
            </button>
          </div>

          {/* Source Modal */}
          {showSourceModal && (
            <div className="modal-overlay" onClick={() => setShowSourceModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Choose Data Source Type</h2>
                <div className="source-types-grid">
                  {sourceTypes.map((type) => (
                    <button
                      key={type.type}
                      className="source-type-card"
                      onClick={() => addSource(type.type)}
                      style={{ borderColor: type.color }}
                    >
                      <div className="type-icon" style={{ color: type.color }}>
                        {type.icon}
                      </div>
                      <div className="type-name">{type.name}</div>
                    </button>
                  ))}
                </div>
                <button onClick={() => setShowSourceModal(false)} className="btn-cancel">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Merge Operations */}
      {step === 2 && (
        <div className="step-content">
          <div className="step-header">
            <h2>🔀 Configure Merge & Transform</h2>
            <button onClick={addMerge} className="btn-primary" disabled={sources.length < 2}>
              + Add Merge
            </button>
          </div>

          {mergeOperations.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔀</div>
              <h3>No merge operations</h3>
              <p>Add a merge to combine your data sources (optional if you have only one source)</p>
            </div>
          ) : (
            <div className="merges-list">
              {mergeOperations.map((merge) => (
                <div key={merge.id} className="merge-card">
                  <div className="merge-header">
                    <h3>{merge.name}</h3>
                    <button onClick={() => removeMerge(merge.id)} className="btn-remove">
                      ×
                    </button>
                  </div>
                  <div className="merge-visual">
                    <div className="merge-source">
                      {sources.find(s => s.id === merge.left_source)?.name || 'Source 1'}
                    </div>
                    <div className="merge-operation">{merge.join_type.toUpperCase()} JOIN</div>
                    <div className="merge-source">
                      {sources.find(s => s.id === merge.right_source)?.name || 'Source 2'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="step-actions">
            <button onClick={() => setStep(1)} className="btn-secondary">
              ← Back
            </button>
            <button onClick={() => setStep(3)} className="btn-primary">
              Next: Quality Checks →
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Quality Checks */}
      {step === 3 && (
        <div className="step-content">
          <div className="step-header">
            <h2>✅ Data Quality Assessment</h2>
            <button 
              onClick={analyzeDataQuality} 
              className="btn-analyze"
              disabled={analyzingQuality || sources.length === 0}
            >
              {analyzingQuality ? '🔍 Analyzing...' : '🔍 Analyze Sample Data (1000 records)'}
            </button>
          </div>

          {/* Quality Analysis Results */}
          {qualityAnalysis && (
            <div className="quality-analysis-results">
              <div className="analysis-header">
                <h3>📊 Quality Analysis Results</h3>
                <span className="sample-info">Based on {qualityAnalysis.sample_size} records</span>
              </div>

              <div className="analysis-grid">
                {qualityAnalysis.field_stats && Object.keys(qualityAnalysis.field_stats).map(fieldName => {
                  const stats = qualityAnalysis.field_stats[fieldName];
                  return (
                    <div key={fieldName} className="field-stats-card">
                      <div className="field-stats-header">
                        <span className="field-name-stat">{fieldName}</span>
                        <span className="field-type-stat">{stats.type}</span>
                      </div>
                      <div className="stats-metrics">
                        <div className="metric">
                          <span className="metric-label">Completeness</span>
                          <div className="metric-bar">
                            <div 
                              className="metric-fill" 
                              style={{
                                width: `${stats.completeness * 100}%`,
                                background: stats.completeness > 0.95 ? '#10b981' : stats.completeness > 0.8 ? '#f59e0b' : '#ef4444'
                              }}
                            />
                          </div>
                          <span className="metric-value">{(stats.completeness * 100).toFixed(1)}%</span>
                        </div>
                        
                        <div className="metric-info">
                          <span>Null: {stats.null_count} | Empty: {stats.empty_count}</span>
                          {stats.false_count > 0 && (
                            <span className="false-warning">⚠️ False values: {stats.false_count}</span>
                          )}
                        </div>

                        {stats.unique_count !== undefined && (
                          <div className="metric">
                            <span className="metric-label">Uniqueness</span>
                            <span className="metric-value">{((stats.unique_count / stats.total_count) * 100).toFixed(1)}%</span>
                          </div>
                        )}

                        {stats.suggested_type && stats.suggested_type !== stats.declared_type && (
                          <div className="type-suggestion">
                            💡 Suggested type: <code>{stats.suggested_type}</code>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {qualityAnalysis.recommendations && qualityAnalysis.recommendations.length > 0 && (
                <div className="recommendations-section">
                  <h4>🎯 Recommended Quality Checks</h4>
                  <div className="recommendations-list">
                    {qualityAnalysis.recommendations.map((rec, idx) => (
                      <div key={idx} className="recommendation-item">
                        <span className="rec-icon">{rec.severity === 'error' ? '🔴' : '🟡'}</span>
                        <span className="rec-text">{rec.message}</span>
                        <button 
                          onClick={() => addQualityCheck(rec.type, rec.field, rec.config)}
                          className="btn-add-rec"
                        >
                          + Add Check
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Manual Quality Checks */}
          <div className="manual-checks-section">
            <h3>Manual Quality Checks ({qualityChecks.length})</h3>
            {qualityChecks.length === 0 ? (
              <div className="empty-state-small">
                <p>Click "Analyze Sample Data" to get automatic recommendations, or add checks manually</p>
              </div>
            ) : (
              <div className="checks-list">
                {qualityChecks.map((check) => (
                  <div key={check.id} className="check-card">
                    <div className="check-header">
                      <div>
                        <h3>{check.name}</h3>
                        <span className="check-type">{check.type}</span>
                        {check.field && <span className="check-field">Field: {check.field}</span>}
                      </div>
                      <button onClick={() => removeCheck(check.id)} className="btn-remove">
                        ×
                      </button>
                    </div>
                    <div className="check-config">
                      Threshold: {(check.threshold * 100).toFixed(0)}% | Severity: {check.severity}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="step-actions">
            <button onClick={() => setStep(2)} className="btn-secondary">
              ← Back
            </button>
            <button onClick={() => setStep(4)} className="btn-primary">
              Next: Review →
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Review & Create */}
      {step === 4 && (
        <div className="step-content">
          <h2>📋 Review & Create Flow</h2>

          <div className="review-section">
            <h3>Flow Information</h3>
            <div className="form-group">
              <label>Flow Name *</label>
              <input
                type="text"
                value={flowName}
                onChange={(e) => setFlowName(e.target.value)}
                placeholder="My Multi-Source Pipeline"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={flowDescription}
                onChange={(e) => setFlowDescription(e.target.value)}
                placeholder="Describe what this pipeline does..."
                className="form-textarea"
                rows="3"
              />
            </div>
          </div>

          <div className="review-section">
            <h3>Pipeline Summary</h3>
            <div className="summary-stats">
              <div className="stat-item">
                <span className="stat-value">{sources.length}</span>
                <span className="stat-label">Data Sources</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{mergeOperations.length}</span>
                <span className="stat-label">Merge Operations</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{qualityChecks.length}</span>
                <span className="stat-label">Quality Checks</span>
              </div>
            </div>

            <div className="pipeline-preview">
              <h4>Pipeline Flow:</h4>
              {sources.map((source, idx) => (
                <div key={source.id}>
                  <div className="preview-node">
                    {sourceTypes.find(t => t.type === source.type)?.icon} {source.name}
                  </div>
                  {idx < sources.length - 1 && <div className="preview-arrow">↓</div>}
                </div>
              ))}
              {mergeOperations.length > 0 && (
                <>
                  <div className="preview-arrow">↓</div>
                  <div className="preview-node">🔀 Merge Operations</div>
                </>
              )}
              {qualityChecks.length > 0 && (
                <>
                  <div className="preview-arrow">↓</div>
                  <div className="preview-node">✅ Quality Checks</div>
                </>
              )}
              <div className="preview-arrow">↓</div>
              <div className="preview-node">📊 Load to BigQuery</div>
            </div>
          </div>

          <div className="step-actions">
            <button onClick={() => setStep(3)} className="btn-secondary">
              ← Back
            </button>
            <button onClick={createFlow} className="btn-primary" disabled={!flowName}>
              ✨ Create Pipeline
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSourceBuilder;

